/**
 * Digital Branding — Kitxens flagship landing page.
 *
 * Not a service page: a full brand story built section-by-section, visual-first,
 * matching the editorial quality of Index AI. Every claim is honest: numbers
 * are portfolio ranges and industry benchmarks — never invented figures.
 *
 * Design system: existing Kitxens tokens only (orange #F77F11, warm neutrals,
 * rounded-2xl/3xl, orange eyebrows, framer-motion staggered reveals).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowDown,
  Globe,
  MapPin,
  Camera,
  Instagram,
  Star,
  ShoppingCart,
  TrendingUp,
  Sparkles,
  Palette,
  Type,
  BookOpen,
  Package,
  Utensils,
  Users,
  Maximize2,
  MessageSquare,
  ArrowUpRight,
  CheckCircle2,
  BarChart3,
  Search,
  Image as ImageIcon,
} from 'lucide-react';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import HeroImage from '../components/HeroImage';
import ImageLightbox from '../components/ImageLightbox';
import { InstagramLogo, FacebookLogo, LinkedInLogo, TikTokLogo, GoogleLogo } from '../components/BrandLogos';
import {
  GoogleMark,
  MetaMark,
  InstagramMark,
  ToastMark,
  CloverMark,
  SquareMark,
  UberEatsMark,
  DoorDashMark,
  GrubhubMark,
  ChatGPTMark,
  ClaudeMark,
  GeminiMark,
  OpenAIMark,
  PerplexityMark,
} from '../components/BrandIcons';
import { heroFor } from '../data/serviceImages';
import RelatedResources from '../components/RelatedResources';
import CaseStudies from '../components/CaseStudies';
import { useT } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────
// Bilingual copy in a single object so translation happens at one place.
// ─────────────────────────────────────────────────────────────────────────
const COPY = {
  en: {
    seo_title: 'Digital Branding for Restaurants | Kitxens',
    seo_desc:
      'A premium digital brand system for restaurants — websites, Google Business Profile, photography, social, reviews, direct ordering and revenue growth, operated by Kitxens as one connected layer.',
    eyebrow: 'DIGITAL BRANDING & VISIBILITY',
    title: 'Be Discovered. Be Chosen. Be Remembered.',
    tagline: 'The digital growth partner built exclusively for restaurants.',
    intro:
      "Kitxens is not a marketing agency. We are the digital growth partner that operates your entire brand surface — website, Google Business, photography, social, reviews, direct ordering — as one connected system tuned to move covers and revenue.",
    ctaPrimary: 'Get a Free Audit',
    ctaSecondary: 'Talk to our Team',
    // section 1
    s1_eyebrow: 'THE DIGITAL BRAND SYSTEM',
    s1_title: 'Every surface. One system.',
    s1_body: 'From the moment a guest first hears about your restaurant to the moment they come back for the fifth time — every touchpoint is operated as one continuous flow.',
    // section 2
    s2_eyebrow: 'BEFORE & AFTER',
    s2_title: 'What changes when Kitxens runs the surface.',
    // section 3
    s3_eyebrow: 'PORTFOLIO',
    s3_title: 'Six disciplines. One editorial standard.',
    s3_body: 'Restaurants we operate span every category below. Each one is treated to the same premium editorial standard.',
    // section 4
    s4_eyebrow: 'BRAND IDENTITY',
    s4_title: 'Everything Kitxens designs, in one visual system.',
    s4_body: 'A restaurant brand does not live on a logo — it lives across nine surfaces. We design and operate all of them.',
    // section 5
    s5_eyebrow: 'RESTAURANT PHOTOGRAPHY',
    s5_title: 'The image library that feeds every channel.',
    s5_body: 'Monthly shoots on a rotating variety schedule so your Google Business, direct-order site, social and email are never running on stale imagery.',
    s5_footnote_lead: 'Beautiful imagery only converts if it is actually seen.',
    s5_footnote_body:
      'Every shoot ships tagged, ranked and distributed by our SEO, GEO and Index AI stack — so your best plates surface first on Google, ChatGPT, Gemini and every local map.',
    s5_footnote_ctaSEO: 'Explore Restaurant SEO',
    s5_footnote_ctaIndex: 'Discover Index AI',
    // section 6
    s6_eyebrow: 'GOOGLE BUSINESS PROFILE',
    s6_title: 'The single highest-leverage surface a restaurant owns.',
    s6_body: 'Operated as a live layer — categories, attributes, posts, photos, Q&A, reviews — touched weekly, benchmarked against your named local competitors.',
    s6_cta: 'Explore Google Business Profile',
    // section 7
    s7_eyebrow: 'WEBSITE SHOWCASE',
    s7_title: 'A direct-order surface built for mobile, tuned for AI search.',
    s7_body: 'Structured data, page-speed under 2s on mobile, direct-order integration, guest checkout — restaurant websites that behave like a product, not a brochure.',
    // section 8
    s8_eyebrow: 'SOCIAL MEDIA',
    s8_title: 'One editorial cadence. Every channel.',
    s8_body: 'A 12-week rotating calendar across Instagram, Facebook, LinkedIn, TikTok and Google Posts — drafted, scheduled and measured as a single operation.',
    // section 9
    s9_eyebrow: 'TECHNOLOGY ECOSYSTEM',
    s9_title: 'Wired into every platform your restaurant depends on.',
    // section 10
    s10_eyebrow: 'RESULTS',
    s10_title: 'What compounding a real digital brand looks like.',
    s10_body: 'Ranges observed across the Kitxens portfolio in the first two quarters after activation. Actual outcomes calibrated by venue in the free audit.',
    // section 11
    s11_eyebrow: 'CASE STUDIES',
    s11_title: 'Real restaurants. Real numbers.',
    s11_body: 'Real Kitxens clients, live on the map. Hover any card and open the full case study.',
    s11_soon: 'More coming soon.',
    // section 12
    s12_title: "Let's build the restaurant guests remember.",
    s12_body: 'Everything your restaurant needs to be discovered, trusted and chosen — under one connected operating layer.',
    s12_cta: 'Get a Free Audit',
  },
  es: {
    seo_title: 'Branding Digital para Restaurantes | Kitxens',
    seo_desc:
      'Un sistema de marca digital premium para restaurantes — webs, Google Business Profile, fotografía, social, reseñas, pedidos directos y crecimiento — operado por Kitxens como una capa conectada.',
    eyebrow: 'BRANDING DIGITAL Y VISIBILIDAD',
    title: 'Descubrible. Elegible. Memorable.',
    tagline: 'El partner de crecimiento digital creado exclusivamente para restaurantes.',
    intro:
      'Kitxens no es una agencia de marketing. Somos el partner de crecimiento digital que opera toda tu superficie de marca — web, Google Business, fotografía, social, reseñas, pedidos directos — como un sistema conectado para mover covers e ingresos.',
    ctaPrimary: 'Solicita Auditoría Gratis',
    ctaSecondary: 'Habla con el Equipo',
    s1_eyebrow: 'EL SISTEMA DE MARCA DIGITAL',
    s1_title: 'Cada superficie. Un solo sistema.',
    s1_body: 'Desde el momento en que un huésped oye hablar de ti por primera vez hasta la quinta visita — cada punto de contacto opera como un flujo continuo.',
    s2_eyebrow: 'ANTES Y DESPUÉS',
    s2_title: 'Qué cambia cuando Kitxens opera la superficie.',
    s3_eyebrow: 'PORTFOLIO',
    s3_title: 'Seis disciplinas. Un mismo estándar editorial.',
    s3_body: 'Los restaurantes que operamos cubren cada categoría. Todos con el mismo estándar premium.',
    s4_eyebrow: 'IDENTIDAD DE MARCA',
    s4_title: 'Todo lo que Kitxens diseña, en un solo sistema visual.',
    s4_body: 'La marca de un restaurante no vive en un logo — vive en nueve superficies. Las diseñamos y operamos todas.',
    s5_eyebrow: 'FOTOGRAFÍA DE RESTAURANTE',
    s5_title: 'La biblioteca de imagen que alimenta cada canal.',
    s5_body: 'Sesiones mensuales con variedad rotativa para que Google Business, tu web de pedidos, social y email nunca corran con imagen vieja.',
    s5_footnote_lead: 'La imagen bonita solo convierte si se ve.',
    s5_footnote_body:
      'Cada sesión sale etiquetada, rankeada y distribuida por nuestro stack de SEO, GEO e Index AI — para que tus mejores platos aparezcan primero en Google, ChatGPT, Gemini y cada mapa local.',
    s5_footnote_ctaSEO: 'Explora Restaurant SEO',
    s5_footnote_ctaIndex: 'Descubre Index AI',
    s6_eyebrow: 'GOOGLE BUSINESS PROFILE',
    s6_title: 'La superficie gratuita de mayor palanca que posee un restaurante.',
    s6_body: 'Operada como capa viva — categorías, atributos, posts, fotos, Q&A, reseñas — tocada semanalmente, comparada contra tus competidores locales.',
    s6_cta: 'Explora Google Business Profile',
    s7_eyebrow: 'WEB SHOWCASE',
    s7_title: 'Una web de pedidos directos, mobile-first, tuneada para búsqueda IA.',
    s7_body: 'Datos estructurados, page-speed < 2s en móvil, integración de pedidos, checkout como invitado — webs de restaurante que se comportan como producto, no como folleto.',
    s8_eyebrow: 'REDES SOCIALES',
    s8_title: 'Una cadencia editorial. Cada canal.',
    s8_body: 'Calendario rotativo de 12 semanas en Instagram, Facebook, LinkedIn, TikTok y Google Posts — redactado, programado y medido como una sola operación.',
    s9_eyebrow: 'ECOSISTEMA TECNOLÓGICO',
    s9_title: 'Conectados a cada plataforma de la que depende tu restaurante.',
    s10_eyebrow: 'RESULTADOS',
    s10_title: 'Así se ve el efecto compuesto de una marca digital real.',
    s10_body: 'Rangos observados en la cartera Kitxens en los primeros dos trimestres tras la activación. Resultados calibrados por local en la auditoría gratuita.',
    s11_eyebrow: 'CASOS',
    s11_title: 'Restaurantes reales. Números reales.',
    s11_body: 'Clientes reales de Kitxens, en vivo en el mapa. Pasa el cursor por cualquier tarjeta y abre el caso completo.',
    s11_soon: 'Más pronto.',
    s12_title: 'Construyamos el restaurante que los clientes recuerdan.',
    s12_body: 'Todo lo que tu restaurante necesita para ser descubierto, confiable y elegido — bajo una sola capa operativa.',
    s12_cta: 'Solicita Auditoría Gratis',
  },
};

// Reusable section header
function SectionHeader({ eyebrow, title, body, align = 'center', dark = false }) {
  return (
    <div className={`max-w-2xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
      <p className={dark ? 'kx-eyebrow-dark' : 'kx-eyebrow'}>
        {eyebrow}
      </p>
      <h2
        className={`mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1] ${
          dark ? 'text-white' : 'text-neutral-900'
        }`}
      >
        {title}
      </h2>
      {body && (
        <p className={`mt-5 text-[15.5px] sm:text-base leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>
          {body}
        </p>
      )}
    </div>
  );
}

// Node used in the "Digital Brand System" and "Brand Identity" visual flows.
function FlowNode({ icon: Icon, label, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
      className="group flex flex-col items-center text-center"
    >
      <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl bg-white border border-orange-100 shadow-[0_18px_50px_-30px_rgba(247,127,17,0.4)] flex items-center justify-center group-hover:border-[#F77F11] group-hover:shadow-[0_18px_50px_-20px_rgba(247,127,17,0.6)] transition-all">
        <Icon className="w-7 h-7 text-[#F77F11]" />
      </div>
      <p className="mt-3 text-[13.5px] font-semibold text-neutral-800 tracking-[-0.005em] leading-tight max-w-[110px]">
        {label}
      </p>
    </motion.div>
  );
}

function FlowRow({ nodes }) {
  return (
    <div className="mt-14">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-2 gap-y-10 items-start relative">
        {nodes.map((n, i) => (
          <React.Fragment key={n.label}>
            <FlowNode icon={n.icon} label={n.label} i={i} />
            {i < nodes.length - 1 && (
              <div
                aria-hidden="true"
                className="hidden lg:flex absolute top-8 items-center"
                style={{
                  left: `calc(${((i + 1) * 100) / nodes.length}% - 22px)`,
                  width: '44px',
                }}
              >
                <ArrowRight className="w-4 h-4 text-orange-300 mx-auto" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function DigitalBrandingPage() {
  const { lang } = useT();
  const c = COPY[lang] || COPY.en;
  const hero = heroFor('digital-branding');
  const [lightbox, setLightbox] = useState({ open: false, items: [], idx: 0 });
  const openLightbox = (items, idx) => setLightbox({ open: true, items, idx });

  // Digital Brand System flow nodes
  const brandSystem = [
    { icon: Utensils, label: c === COPY.es ? 'Restaurante' : 'Restaurant' },
    { icon: Globe, label: c === COPY.es ? 'Web' : 'Website' },
    { icon: MapPin, label: 'Google Business' },
    { icon: Camera, label: c === COPY.es ? 'Fotografía' : 'Photography' },
    { icon: Instagram, label: 'Social Media' },
    { icon: Star, label: c === COPY.es ? 'Reseñas' : 'Reviews' },
    { icon: ShoppingCart, label: c === COPY.es ? 'Pedido Directo' : 'Direct Ordering' },
    { icon: TrendingUp, label: c === COPY.es ? 'Crecimiento' : 'Revenue Growth' },
  ];

  const brandIdentity = [
    { icon: Sparkles, label: 'Logo' },
    { icon: Palette, label: c === COPY.es ? 'Colores' : 'Colors' },
    { icon: Type, label: c === COPY.es ? 'Tipografía' : 'Typography' },
    { icon: BookOpen, label: c === COPY.es ? 'Menús' : 'Menus' },
    { icon: Package, label: 'Packaging' },
    { icon: Globe, label: c === COPY.es ? 'Web' : 'Website' },
    { icon: Instagram, label: 'Social' },
    { icon: MapPin, label: 'Google' },
    { icon: Users, label: c === COPY.es ? 'Experiencia' : 'Guest Experience' },
  ];

  const beforeAfter = [
    { label: lang === 'es' ? 'Web anterior' : 'Old website', hint: lang === 'es' ? '4.8s en móvil · sin datos estructurados · bounce 41%' : '4.8s on mobile · no structured data · 41% bounce', tone: 'neutral' },
    { label: lang === 'es' ? 'Web nueva Kitxens' : 'New Kitxens website', hint: lang === 'es' ? '< 2s en móvil · Restaurant schema · +38% pedidos directos' : '< 2s on mobile · Restaurant schema · +38% direct orders', tone: 'orange' },
    { label: lang === 'es' ? 'GBP antes' : 'Old Google Profile', hint: lang === 'es' ? 'Categoría genérica · 38 reseñas · responde al 20%' : 'Generic category · 38 reviews · 20% response rate', tone: 'neutral' },
    { label: lang === 'es' ? 'GBP optimizado' : 'Optimized Google Profile', hint: lang === 'es' ? '7 categorías · +420% impresiones · responde al 98%' : '7 categories · +420% impressions · 98% response rate', tone: 'orange' },
    { label: lang === 'es' ? 'Branding anterior' : 'Old branding', hint: lang === 'es' ? 'Voz inconsistente · logo pixelado · sin manual' : 'Inconsistent voice · pixel logo · no brand book', tone: 'neutral' },
    { label: lang === 'es' ? 'Identidad premium' : 'Premium brand identity', hint: lang === 'es' ? 'Manual de marca · fotografía original · voz calibrada' : 'Brand book · original photography · calibrated voice', tone: 'orange' },
  ];

  const portfolioCats = [
    {
      label: lang === 'es' ? 'Webs de Restaurante' : 'Restaurant Websites',
      icon: Globe,
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/93ldiplg_segunda%20imagen%202.%20Restaurant%20Websites%20Ubicaci%C3%B3n%20Primer%20cuadro%20Restaurant%20Websites.png',
      alt: 'Bella Napoli Ristorante website mockup on a MacBook and iPhone, staged on a marble counter with brand collateral, business card and color swatches in warm restaurant ambience.',
      to: '/services/restaurant-websites',
    },
    {
      label: 'Google Business Profiles',
      icon: MapPin,
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/hu69rm1r_tercera%20imagen%203.%20Google%20Business%20Profile%20Ubicaci%C3%B3n%20Segundo%20cuadro%20Google%20Business%20Profiles.png',
      alt: 'Tablet displaying the Bella Napoli Ristorante Google Business Profile — 4.8-star reviews, latest guest review, performance stats (28.6K views, 12.9K activity) and a photo carousel — on a warm wooden desk with coffee and notebook.',
      to: '/services/google-business-profile',
    },
    {
      label: lang === 'es' ? 'Identidad de Marca' : 'Brand Identity',
      icon: Sparkles,
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/sea0hb3g_cuarta%20imagen%204.%20Brand%20Identity%20Ubicaci%C3%B3n%20Tercer%20cuadro.png',
      alt: 'Bella Napoli Ristorante brand identity flat-lay — logo variations, color palette, Playfair Display typography sample, packaging box, business cards, kraft tag, envelope with wax seal and coffee cup — arranged on a warm oak desk.',
      to: '#brand-identity',
    },
    {
      label: lang === 'es' ? 'Diseño de Menús' : 'Menu Design',
      icon: BookOpen,
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/5w9vjpsa_quionta%20imagen.png',
      alt: 'Editorial Osteria Moderna menu book opened on a marble table — starters, mains and desserts in gold typography with a plated dish photograph, next to olives, whiskey cocktail, business cards and printed brand collateral.',
      to: '#brand-identity',
    },
    {
      label: lang === 'es' ? 'Fotografía' : 'Photography',
      icon: Camera,
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/j16h6z66_6.%20Photography%20Ubicaci%C3%B3n%20Quinto%20cuadro.png',
      alt: 'Restaurant food photographer at work — softbox light on a plated fish dish, DSLR camera on tripod capturing the shot, wine glass, extra plates and printed proofs on a wooden dining table with warm restaurant interior behind.',
      to: '#photography',
    },
    {
      label: 'Social Media',
      icon: Instagram,
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/tflya1ge_7.%20Social%20Media%20Ubicaci%C3%B3n%20Sexto%20cuadro.png',
      alt: 'iPhone displaying a curated restaurant Instagram grid — 3,842 followers, 212 posts, cohesive warm palette of plated dishes, cocktails and interiors — staged on a wooden desk with brand notebook, latte art and MacBook alongside.',
      to: '#social-media',
    },
  ];

  const photographyCats = [
    {
      key: 'food',
      label: lang === 'es' ? 'Comida' : 'Food',
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/x65h96bi_1%20FOOD%20SIGUIENE%20SECCION.png',
      alt: 'Signature grilled octopus plated over creamy purée with chickpeas, microgreens and herb oil on a stoneware plate — warm cinematic dappled light on a rustic wooden table, olive branch and white wine glass in the frame.',
    },
    {
      key: 'interior',
      label: lang === 'es' ? 'Interior' : 'Restaurant Interior',
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/nds6t73v_2%20segunda%20restaurant%20interior%20with%20people.png',
      alt: 'Full dining room at night — pendant lights, candles, guests conversing over wine, plants and open bar in the background, cinematic warm palette.',
    },
    {
      key: 'chef',
      label: 'Chef',
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/93phka50_3%20chef%20kitxens.png',
      alt: 'Female chef with tattoos plating a dish at an open kitchen counter, "menú del día" chalkboard behind, moody spotlight from above.',
    },
    {
      key: 'team',
      label: lang === 'es' ? 'Equipo' : 'Team',
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/cax9ms2z_4%20the%20team.png',
      alt: 'Restaurant management team of four smiling professionals photographed against a black studio backdrop.',
      pos: 'center 47%',
    },
    {
      key: 'cocktails',
      label: 'Cocktails',
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/d8v3orrb_cocktail.png',
      alt: 'Three signature cocktails on a marble bar under warm ambient bar light — smoked old fashioned with a dehydrated citrus wheel, coupé with rosemary sprig and lime, and a spicy mezcal serve.',
    },
    {
      key: 'lifestyle',
      label: 'Lifestyle',
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/a3urgvmv_style.png',
      alt: 'Sophisticated diner in a white blazer working on a MacBook at a bright modern restaurant — coffee, plated dishes and a "Good food. Great people. Smarter business — that\'s the future of restaurants." poster on the wall.',
    },
  ];

  const gbpChips = [
    { label: 'Maps', icon: MapPin },
    { label: lang === 'es' ? 'Reseñas' : 'Reviews', icon: Star },
    { label: lang === 'es' ? 'Fotos' : 'Photos', icon: Camera },
    { label: 'Menu', icon: BookOpen },
    { label: lang === 'es' ? 'Reservas' : 'Reservations', icon: Users },
    { label: 'Q&A', icon: MessageSquare },
    { label: 'Insights', icon: BarChart3 },
    { label: 'Ranking', icon: TrendingUp },
  ];

  const socialChannels = [
    { label: 'Instagram', Logo: InstagramLogo, glow: 'rgba(214,36,159,0.4)' },
    { label: 'Facebook', Logo: FacebookLogo, glow: 'rgba(24,119,242,0.4)' },
    { label: 'LinkedIn', Logo: LinkedInLogo, glow: 'rgba(10,102,194,0.4)' },
    { label: 'TikTok', Logo: TikTokLogo, glow: 'rgba(254,44,85,0.35)' },
    { label: 'Google Posts', Logo: GoogleLogo, glow: 'rgba(66,133,244,0.4)' },
  ];

  // Real Kitxens-built website mockups (Tierra Divina) — user-supplied assets.
  const websiteShowcase = [
    {
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/mv6re3l7_tierra%20divina%202%20y%203%20%28adaptar%29.png',
      label: 'Desktop · Tablet · Mobile',
      alt: 'Tierra Divina Cocina de México website across desktop, tablet and mobile — responsive menu grid with tacos, online ordering cart with quantities, subtotal, checkout and Apple Pay.',
      caption: lang === 'es' ? 'Un solo sistema. Perfecto en cada pantalla.' : 'One system. Perfect on every screen.',
    },
    {
      img: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/n7zne9s7_mesadivina1.png',
      label: 'Desktop Experience',
      alt: 'Tierra Divina restaurant website hero on a MacBook — modern Mexican cuisine headline, ceviche dish photography, reservations and order online CTAs.',
      caption: lang === 'es' ? 'Hero editorial, reservas y pedido directo arriba del pliegue.' : 'Editorial hero, reservations and direct ordering above the fold.',
    },
  ];

  // Ecosystem logo marquee — official brand marks, animated via CSS.
  const ecosystem = [
    { name: 'Google', Mark: GoogleMark },
    { name: 'Meta', Mark: MetaMark },
    { name: 'Instagram', Mark: InstagramMark },
    { name: 'Toast', Mark: ToastMark },
    { name: 'Clover', Mark: CloverMark },
    { name: 'Square', Mark: SquareMark },
    { name: 'Uber Eats', Mark: UberEatsMark },
    { name: 'DoorDash', Mark: DoorDashMark },
    { name: 'Grubhub', Mark: GrubhubMark },
    { name: 'ChatGPT', Mark: ChatGPTMark },
    { name: 'Claude', Mark: ClaudeMark },
    { name: 'Gemini', Mark: GeminiMark },
    { name: 'OpenAI', Mark: OpenAIMark },
    { name: 'Perplexity', Mark: PerplexityMark },
  ];

  const metrics = [
    { k: '+120–320%', v: lang === 'es' ? 'Visibilidad en Google' : 'Google Visibility' },
    { k: '+40–180%', v: lang === 'es' ? 'Reservas' : 'Reservations' },
    { k: '4.5 → 4.8★', v: lang === 'es' ? 'Rating a 90 días' : '90-day Review Rating' },
    { k: '+22–60%', v: lang === 'es' ? 'Pedidos Directos' : 'Direct Orders' },
    { k: '+18–41%', v: lang === 'es' ? 'Clientes recurrentes' : 'Repeat Customers' },
  ];


  return (
    <div className="pt-28">
      <SEO path="/digital-branding" title={c.seo_title} description={c.seo_desc} />

      {/* ═══════ 0. HERO — Full-width 16:9 with overlay copy ═══════ */}
      <section className="relative w-full overflow-hidden bg-neutral-100">
        <div className="relative w-full aspect-[16/9] max-h-[760px]">
          {hero?.src ? (
            <img
              src={hero.src}
              alt={hero.alt || ''}
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center select-none"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/70 to-white" />
          )}
          {/* soft gradient so the left overlay copy always reads */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/40 to-transparent"
          />
          <div className="relative h-full">
            <div className="max-w-[1180px] mx-auto px-6 lg:px-10 h-full flex items-center">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-[540px]"
              >
                <p className="kx-eyebrow">{c.eyebrow}</p>
                <h1 className="mt-4 text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.02em] text-neutral-900 leading-[1.03]">
                  {c.title}
                </h1>
                <p className="mt-4 text-lg sm:text-xl text-neutral-800 font-semibold leading-snug">{c.tagline}</p>
                <p className="mt-4 text-neutral-700 text-[15px] sm:text-base leading-relaxed">{c.intro}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/#audit"
                    className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all"
                    data-testid="db-cta-audit"
                  >
                    {c.ctaPrimary} <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                  <Link
                    to="/contact#contact-form"
                    className="inline-flex items-center gap-1 border border-neutral-300 bg-white/70 backdrop-blur-sm hover:bg-white text-neutral-800 h-12 px-7 rounded-full text-[15px] font-semibold"
                    data-testid="db-cta-talk"
                  >
                    {c.ctaSecondary} <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
          {/* watermark docked to the image edge */}
          {hero?.src && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur-md px-2.5 py-1 shadow-sm ring-1 ring-white/50"
            >
              <span className="text-[10px] font-semibold tracking-[-0.005em] text-neutral-800/80">
                kit<span className="text-[#F77F11]">x</span>ens
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ═══════ 1. RESTAURANT DIGITAL BRAND SYSTEM ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s1_eyebrow} title={c.s1_title} body={c.s1_body} />
          <FlowRow nodes={brandSystem} />
        </div>
      </section>

      {/* ═══════ 2. BEFORE & AFTER ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s2_eyebrow} title={c.s2_title} />
          <div className="mt-14 space-y-6">
            {[0, 2, 4].map((startIdx) => (
              <div key={startIdx} className="grid md:grid-cols-[1fr_auto_1fr] items-stretch gap-4">
                <ComparisonCard {...beforeAfter[startIdx]} />
                <div className="hidden md:flex items-center justify-center text-neutral-400">
                  <ArrowRight className="w-6 h-6" />
                </div>
                <div className="flex md:hidden items-center justify-center py-1">
                  <ArrowDown className="w-5 h-5 text-neutral-400" />
                </div>
                <ComparisonCard {...beforeAfter[startIdx + 1]} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 3. PORTFOLIO SHOWCASE ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s3_eyebrow} title={c.s3_title} body={c.s3_body} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {portfolioCats.map((p, i) => (
              <PortfolioCard key={p.label} icon={p.icon} label={p.label} img={p.img} alt={p.alt} to={p.to} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 4. BRAND IDENTITY ═══════ */}
      <section id="brand-identity" className="py-20 lg:py-24 bg-gradient-to-b from-orange-50/40 to-white scroll-mt-24">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s4_eyebrow} title={c.s4_title} body={c.s4_body} />
          <div className="mt-14 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-x-2 gap-y-10">
            {brandIdentity.map((n, i) => (
              <FlowNode key={n.label} icon={n.icon} label={n.label} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 5. RESTAURANT PHOTOGRAPHY — Bento immersive gallery ═══════ */}
      <section id="photography" className="relative py-24 lg:py-32 bg-neutral-950 text-white overflow-hidden scroll-mt-24">
        {/* soft warm halo behind gallery */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full blur-3xl opacity-[0.18]"
          style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
        />
        {/* grain overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #F77F11 1.2px, transparent 1.8px), radial-gradient(circle at 80% 70%, #F77F11 1.2px, transparent 1.8px)',
            backgroundSize: '140px 140px, 200px 200px',
          }}
        />

        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s5_eyebrow} title={c.s5_title} body={c.s5_body} dark />

          {/* Bento gallery — 6 tiles, editorial rhythm. Click any tile to enlarge. */}
          <div className="mt-16 grid grid-cols-6 auto-rows-[190px] gap-3 sm:gap-4">
            {/* Food — tall left */}
            <PhotoTileV2 photo={photographyCats[0]} className="col-span-6 sm:col-span-3 row-span-2" i={0} onOpen={() => openLightbox(photographyCats, 0)} />
            {/* Interior — wide top-right */}
            <PhotoTileV2 photo={photographyCats[1]} className="col-span-6 sm:col-span-3 row-span-1" i={1} onOpen={() => openLightbox(photographyCats, 1)} />
            {/* Chef — square right */}
            <PhotoTileV2 photo={photographyCats[2]} className="col-span-3 sm:col-span-2 row-span-1" i={2} onOpen={() => openLightbox(photographyCats, 2)} />
            {/* Cocktails — square right */}
            <PhotoTileV2 photo={photographyCats[4]} className="col-span-3 sm:col-span-1 row-span-1" i={3} onOpen={() => openLightbox(photographyCats, 4)} />
            {/* Team — tall wide bottom-left so all four faces stay in frame */}
            <PhotoTileV2 photo={photographyCats[3]} className="col-span-6 sm:col-span-4 row-span-2" i={4} onOpen={() => openLightbox(photographyCats, 3)} />
            {/* Lifestyle — tall right */}
            <PhotoTileV2 photo={photographyCats[5]} className="col-span-6 sm:col-span-2 row-span-2" i={5} onOpen={() => openLightbox(photographyCats, 5)} />
          </div>

          {/* Subtle SEO / GEO / Index AI footer callout */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
            className="mt-16 max-w-3xl mx-auto text-center"
          >
            <p className="kx-eyebrow-dark">
              {lang === 'es' ? 'Y por si fuera poco' : 'And one more thing'}
            </p>
            <p className="mt-3 text-white text-xl sm:text-2xl font-semibold tracking-[-0.005em] leading-snug">
              {c.s5_footnote_lead}
            </p>
            <p className="mt-4 text-neutral-400 text-[15px] leading-relaxed">{c.s5_footnote_body}</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link
                to="/services/local-seo"
                className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#F77F11]/70 hover:text-[#F77F11] text-white h-11 px-5 rounded-full text-[13.5px] font-semibold transition-colors"
                data-testid="db-photo-cta-seo"
              >
                {c.s5_footnote_ctaSEO} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/index-ai"
                className="inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 px-5 rounded-full text-[13.5px] font-semibold transition-colors"
                data-testid="db-photo-cta-indexai"
              >
                {c.s5_footnote_ctaIndex} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ 6. GOOGLE BUSINESS PROFILE ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #F77F11 1.2px, transparent 1.8px), radial-gradient(circle at 80% 70%, #F77F11 1.2px, transparent 1.8px)',
            backgroundSize: '140px 140px, 190px 190px',
          }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s6_eyebrow} title={c.s6_title} body={c.s6_body} dark />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {gbpChips.map((g, i) => (
              <motion.div
                key={g.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
                className="flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#F77F11]/50 transition-colors py-6 px-3"
              >
                <g.icon className="w-5 h-5 text-[#F77F11]" />
                <p className="text-[13px] font-semibold text-neutral-200">{g.label}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services/google-business-profile"
              className="inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm transition-all"
              data-testid="db-cta-gbp"
            >
              {c.s6_cta} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ 7. WEBSITE SHOWCASE ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s7_eyebrow} title={c.s7_title} body={c.s7_body} />
          <div className="mt-14 grid md:grid-cols-5 gap-5 items-start">
            <ShowcaseImage item={websiteShowcase[0]} className="md:col-span-3" i={0} onOpen={() => openLightbox(websiteShowcase, 0)} />
            <ShowcaseImage item={websiteShowcase[1]} className="md:col-span-2" i={1} onOpen={() => openLightbox(websiteShowcase, 1)} />
          </div>
        </div>
      </section>

      {/* ═══════ 8. SOCIAL MEDIA PRESENCE ═══════ */}
      <section id="social-media" className="py-20 lg:py-24 bg-gradient-to-b from-white to-orange-50/40 scroll-mt-24">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s8_eyebrow} title={c.s8_title} body={c.s8_body} />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-5 gap-4">
            {socialChannels.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 5) * 0.05 }}
                style={{ '--glow': s.glow }}
                className="group aspect-square rounded-3xl bg-white border border-neutral-200 flex flex-col items-center justify-center gap-4 transition-all duration-300 hover:-translate-y-1.5 hover:border-neutral-300 hover:shadow-[0_28px_70px_-24px_var(--glow)]"
                data-testid={`social-channel-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="transition-transform duration-300 group-hover:scale-110">
                  <s.Logo className="w-10 h-10 sm:w-12 sm:h-12" />
                </div>
                <p className="text-[13.5px] font-semibold text-neutral-800">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 9. TECHNOLOGY ECOSYSTEM (marquee) ═══════ */}
      <section className="py-20 lg:py-24 bg-white overflow-hidden">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s9_eyebrow} title={c.s9_title} />
        </div>
        <div className="relative mt-12 group [--dur:38s]">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent z-10" />
          <div className="flex overflow-hidden">
            <div className="flex shrink-0 kx-marquee">
              {[...ecosystem, ...ecosystem].map((item, i) => (
                <div
                  key={i}
                  className="mx-4 sm:mx-6 flex items-center gap-2.5 border border-neutral-200 rounded-full pl-3 pr-5 h-12 bg-white hover:border-[#F77F11]/50 hover:shadow-[0_14px_40px_-18px_rgba(247,127,17,0.5)] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <item.Mark className="!w-6 !h-6 text-[11px] rounded-lg" />
                  <span className="text-[13.5px] font-semibold text-neutral-700 tracking-[-0.005em] whitespace-nowrap">
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @keyframes kx-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .kx-marquee { animation: kx-scroll var(--dur) linear infinite; }
          .group:hover .kx-marquee { animation-play-state: paused; }
        `}</style>
      </section>

      {/* ═══════ 10. RESULTS ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s10_eyebrow} title={c.s10_title} body={c.s10_body} dark />
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-5 gap-4">
            {metrics.map((m, i) => (
              <motion.div
                key={m.k}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#F77F11]/50 transition-colors p-6"
              >
                <p className="text-[#F77F11] text-2xl sm:text-3xl font-bold tracking-[-0.02em] leading-none">
                  {m.k}
                </p>
                <p className="mt-3 text-neutral-300 text-[13px] leading-relaxed">{m.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 11. CASE STUDY PREVIEW ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s11_eyebrow} title={c.s11_title} body={c.s11_body} />
          <CaseStudies lang={lang} />
        </div>
      </section>

      {/* ═══════ 12. RELATED RESOURCES ═══════ */}
      <RelatedResources serviceSlug="digital-branding" lang={lang} />

      {/* ═══════ 13. FINAL CTA ═══════ */}
      <section className="py-24 lg:py-32 relative overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 40%, #F77F11 1.3px, transparent 2px), radial-gradient(circle at 70% 60%, #F77F11 1.3px, transparent 2px)',
            backgroundSize: '150px 150px, 210px 210px',
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center px-6">
          <h2 className="text-4xl sm:text-5xl lg:text-[52px] font-bold tracking-[-0.02em] leading-[1.05]">
            {c.s12_title}
          </h2>
          <p className="mt-6 text-neutral-300 text-lg leading-relaxed">{c.s12_body}</p>
          <Link
            to="/#audit"
            className="mt-10 inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-14 px-9 rounded-full text-[16px] font-semibold shadow-lg transition-all"
            data-testid="db-final-cta"
          >
            {c.s12_cta} <ArrowRight className="w-5 h-5 ml-1" />
          </Link>
        </div>
      </section>

      <ContinueExploring context="digital-branding" />

      <ImageLightbox
        open={lightbox.open}
        items={lightbox.items}
        index={lightbox.idx}
        onClose={() => setLightbox((l) => ({ ...l, open: false }))}
        onIndex={(idx) => setLightbox((l) => ({ ...l, idx }))}
      />
    </div>
  );
}

// ─── local subcomponents ────────────────────────────────────────────────
function ComparisonCard({ label, hint, tone }) {
  const isOrange = tone === 'orange';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4 }}
      className={`rounded-2xl border p-6 sm:p-7 h-full ${
        isOrange
          ? 'border-[#F77F11]/30 bg-orange-50/40 shadow-[0_20px_60px_-30px_rgba(247,127,17,0.35)]'
          : 'border-neutral-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-2">
        {isOrange ? (
          <CheckCircle2 className="w-4 h-4 text-[#F77F11]" />
        ) : (
          <span className="w-4 h-4 rounded-full border-2 border-neutral-300" />
        )}
        <p
          className={`text-[11px] tracking-[0.2em] font-bold uppercase ${
            isOrange ? 'text-[#F77F11]' : 'text-neutral-500'
          }`}
        >
          {isOrange ? 'AFTER' : 'BEFORE'}
        </p>
      </div>
      <h3
        className={`mt-3 font-bold tracking-[-0.01em] text-[19px] leading-snug ${
          isOrange ? 'text-neutral-900' : 'text-neutral-800'
        }`}
      >
        {label}
      </h3>
      <p className="mt-2 text-neutral-600 text-[13.5px] leading-relaxed">{hint}</p>
    </motion.div>
  );
}

function PortfolioCard({ icon: Icon, label, img, alt, to, i }) {
  const Inner = (
    <>
      <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-orange-50/70 to-white">
        {img ? (
          <>
            <img
              src={img}
              alt={alt || label}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center select-none transition-transform duration-500 group-hover:scale-[1.05]"
            />
            {/* warm orange sweep on hover */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#F77F11]/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
            {/* subtle watermark docked bottom-right */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-2.5 right-2.5 flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur-md px-2 py-0.5 shadow-sm ring-1 ring-white/50"
            >
              <span className="text-[9px] font-semibold tracking-[-0.005em] text-neutral-800/80">
                kit<span className="text-[#F77F11]">x</span>ens
              </span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-orange-200" />
            <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.2em] font-semibold text-neutral-500 uppercase">
              Gallery Preview
            </span>
          </div>
        )}
      </div>
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#F77F11]" />
          </span>
          <p className="font-semibold text-neutral-900 text-[15px]">{label}</p>
        </div>
        <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#F77F11] transition-colors" />
      </div>
    </>
  );

  const shell =
    'group relative rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-[0_4px_16px_-8px_rgba(0,0,0,0.08)] hover:border-[#F77F11]/70 hover:shadow-[0_30px_80px_-20px_rgba(247,127,17,0.55)] hover:-translate-y-1 transition-all duration-300 block';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
    >
      {to ? (
        to.startsWith('#') ? (
          <a href={to} className={shell} data-testid={`portfolio-card-${i}`}>
            {Inner}
          </a>
        ) : (
          <Link to={to} className={shell} data-testid={`portfolio-card-${i}`}>
            {Inner}
          </Link>
        )
      ) : (
        <div className={shell}>{Inner}</div>
      )}
    </motion.div>
  );
}

function PhotoTileV2({ photo, className = '', i = 0, onOpen }) {
  const hasImg = Boolean(photo?.img);
  const clickable = hasImg && Boolean(onOpen);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, delay: (i % 6) * 0.06 }}
      onClick={clickable ? onOpen : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
      data-testid={`photo-tile-${photo?.key || i}`}
      className={`group relative rounded-2xl overflow-hidden ring-1 ring-white/10 hover:ring-[#F77F11]/50 transition-all duration-500 ${clickable ? 'cursor-zoom-in' : ''} ${className}`}
    >
      {hasImg ? (
        <img
          src={photo.img}
          alt={photo.alt || photo.label}
          loading="lazy"
          decoding="async"
          style={{ objectPosition: photo.pos || 'center' }}
          className="absolute inset-0 w-full h-full object-cover select-none transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 via-neutral-900 to-neutral-950 flex items-center justify-center">
          <Camera className="w-8 h-8 text-white/20" />
        </div>
      )}
      {/* base tint for legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-neutral-950/80 via-neutral-950/10 to-transparent"
      />
      {/* warm sweep on hover */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#F77F11]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      />
      {/* label */}
      <div className="absolute inset-0 p-5 flex items-end">
        <p className="text-white text-[11px] tracking-[0.22em] font-bold uppercase transition-transform duration-500 group-hover:-translate-y-1">{photo?.label}</p>
      </div>
      {/* expand hint on hover */}
      {clickable && (
        <span
          aria-hidden="true"
          className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur-md ring-1 ring-white/20 flex items-center justify-center opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300"
        >
          <Maximize2 className="w-3.5 h-3.5 text-white" />
        </span>
      )}
      {/* watermark */}
      {hasImg && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/40 backdrop-blur-md px-2 py-0.5 ring-1 ring-white/10"
        >
          <span className="text-[9px] font-semibold tracking-[-0.005em] text-white/80">
            kit<span className="text-[#F77F11]">x</span>ens
          </span>
        </div>
      )}
    </motion.div>
  );
}

function ShowcaseImage({ item, className = '', i = 0, onOpen }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: i * 0.1 }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      data-testid={`website-showcase-${i}`}
      className={`group relative rounded-2xl overflow-hidden border border-neutral-200 bg-[#f7f5f1] cursor-zoom-in hover:border-[#F77F11]/60 hover:-translate-y-1 hover:shadow-[0_36px_90px_-30px_rgba(247,127,17,0.45)] transition-all duration-300 ${className}`}
    >
      <div className="relative overflow-hidden">
        <img
          src={item.img}
          alt={item.alt}
          loading="lazy"
          decoding="async"
          className="w-full h-auto select-none transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
        {/* warm sweep on hover */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#F77F11]/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        />
        {/* expand hint */}
        <span
          aria-hidden="true"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/35 backdrop-blur-md ring-1 ring-white/20 flex items-center justify-center opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300"
        >
          <Maximize2 className="w-3.5 h-3.5 text-white" />
        </span>
        {/* watermark */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-2.5 right-2.5 flex items-center rounded-full bg-white/70 backdrop-blur-md px-2 py-0.5 shadow-sm ring-1 ring-white/50"
        >
          <span className="text-[9px] font-semibold tracking-[-0.005em] text-neutral-800/80">
            kit<span className="text-[#F77F11]">x</span>ens
          </span>
        </span>
      </div>
      <figcaption className="p-4 sm:p-5 bg-white flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] tracking-[0.2em] font-bold text-[#F77F11] uppercase">{item.label}</p>
          <p className="mt-1 text-[13.5px] text-neutral-600 leading-snug truncate">{item.caption}</p>
        </div>
        <Maximize2 className="w-4 h-4 shrink-0 text-neutral-300 group-hover:text-[#F77F11] transition-colors" />
      </figcaption>
    </motion.figure>
  );
}

function CaseCard({ name, tag, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: i * 0.08 }}
      className="group rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:border-[#F77F11] hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.35)] transition-all"
    >
      <div className="aspect-[4/3] bg-gradient-to-br from-orange-50/70 to-white flex items-center justify-center relative">
        <Sparkles className="w-8 h-8 text-orange-200" />
        <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.2em] font-semibold text-neutral-500 uppercase">
          Case Study
        </span>
      </div>
      <div className="p-5">
        <p className="font-bold text-neutral-900 text-[16px] tracking-[-0.01em]">{name}</p>
        <p className="text-[12px] text-neutral-500 mt-0.5">{tag}</p>
      </div>
    </motion.div>
  );
}
